// src/services/TutorialService.ts
/**
 * 📚 Tutorial Service
 * Manages tutorial progress and step completion
 */

import { supabase } from '../config/supabase';

/** Fired after the page-tutorial flags are cleared. See PageTourManager. */
export const TUTORIALS_RESET_EVENT = 'tradingspace:tutorials-reset';

/** Fired after every tour has been marked seen, so the managers stop asking. */
export const TUTORIALS_DISMISSED_EVENT = 'tradingspace:tutorials-dismissed';

/**
 * Every page that owns a tour, including the first-run one.
 *
 * Kept in one place because three things need the same list: replaying,
 * dismissing, and knowing when there is nothing left to show.
 */
export const ALL_TOUR_PAGES = [
  'first_run_tour',
  'dashboard',
  'charts',
  'data',
  'diary',
  'tajir',
  'report',
  'settings',
  'platforms',
] as const;

export type TutorialStep =
  | 'first_file_upload'
  | 'first_report'
  | 'first_comparison'
  | 'first_diary'
  | 'first_custom_column'
  | 'tutorial_shown'
  | 'page_tutorial_dashboard'
  | 'page_tutorial_charts'
  | 'page_tutorial_data'
  | 'page_tutorial_platforms'
  | 'page_tutorial_diary'
  | 'page_tutorial_report'
  | 'page_tutorial_settings'
  | 'page_tutorial_tajir'
  | 'page_tutorial_first_run_tour';

export class TutorialService {
  /**
   * Mark a tutorial step as completed
   */
  static async completeStep(userId: string, stepName: TutorialStep): Promise<void> {
    try {
      const { error } = await supabase.rpc('complete_tutorial_step', {
        p_user_id: userId,
        p_step_name: stepName
      });

      if (error) throw error;

      console.log(`✅ Tutorial step completed: ${stepName}`);
    } catch (error: any) {
      console.error(`❌ Error completing tutorial step ${stepName}:`, error.message);
    }
  }

  /**
   * Get tutorial progress for user
   */
  static async getProgress(userId: string): Promise<{
    step_name: string;
    completed: boolean;
    completed_at: string | null;
  }[]> {
    try {
      const { data, error } = await supabase.rpc('get_tutorial_progress', {
        p_user_id: userId
      });

      if (error) throw error;

      return data || [];
    } catch (error: any) {
      console.error('❌ Error getting tutorial progress:', error.message);
      return [];
    }
  }

  /**
   * Get completion percentage
   */
  static async getCompletionPercentage(userId: string): Promise<{
    total_steps: number;
    completed_steps: number;
    percentage: number;
    steps: any[];
  } | null> {
    try {
      const { data, error } = await supabase.rpc('get_onboarding_completion', {
        p_user_id: userId
      });

      if (error) throw error;

      return data && data.length > 0 ? data[0] : null;
    } catch (error: any) {
      console.error('❌ Error getting completion percentage:', error.message);
      return null;
    }
  }

  /**
   * Check if user has completed onboarding questionnaire
   */
  static async hasCompletedQuestionnaire(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_onboarding')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      return !!data;
    } catch (error: any) {
      console.error('❌ Error checking questionnaire completion:', error.message);
      return false;
    }
  }

  /**
   * Get user onboarding profile
   */
  static async getOnboardingProfile(userId: string): Promise<{
    trader_type?: string;
    experience_level?: string;
    time_dedicated?: string;
    main_goal?: string;
    broker_used?: string;
    broker_other?: string;
  } | null> {
    try {
      const { data, error } = await supabase
        .from('user_onboarding')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      return data || null;
    } catch (error: any) {
      console.error('❌ Error getting onboarding profile:', error.message);
      return null;
    }
  }

  /**
   * Check if user should see welcome tutorial
   * Returns true if user completed questionnaire but hasn't seen the tutorial yet
   */
  static async shouldShowWelcomeTutorial(userId: string): Promise<boolean> {
    try {
      const hasQuestionnaire = await this.hasCompletedQuestionnaire(userId);
      const progress = await this.getProgress(userId);
      const tutorialShown = progress.some(step => step.step_name === 'tutorial_shown' && step.completed);
      return hasQuestionnaire && !tutorialShown;
    } catch (error: any) {
      console.error('❌ Error checking welcome tutorial:', error.message);
      return false;
    }
  }

  /**
   * Check if tutorial has been completed (tutorial_shown flag)
   * Used to prevent showing tutorial multiple times
   */
  static async hasTutorialBeenShown(userId: string): Promise<boolean> {
    try {
      const progress = await this.getProgress(userId);
      return progress.some(step => step.step_name === 'tutorial_shown' && step.completed);
    } catch (error: any) {
      console.error('❌ Error checking tutorial status:', error.message);
      return false;
    }
  }

  /**
   * Check if a specific page tutorial has been shown
   * @param userId User ID
   * @param page Page name (e.g., 'dashboard', 'charts', 'data')
   */
  static async hasPageTutorialBeenShown(userId: string, page: string): Promise<boolean> {
    try {
      const stepName = `page_tutorial_${page}` as TutorialStep;
      const progress = await this.getProgress(userId);
      return progress.some(step => step.step_name === stepName && step.completed);
    } catch (error: any) {
      console.error(`❌ Error checking page tutorial for ${page}:`, error.message);
      return false;
    }
  }

  /**
   * Mark a page tutorial as shown
   * @param userId User ID
   * @param page Page name (e.g., 'dashboard', 'charts', 'data')
   */
  static async markPageTutorialShown(userId: string, page: string): Promise<void> {
    try {
      const stepName = `page_tutorial_${page}` as TutorialStep;
      await this.completeStep(userId, stepName);
    } catch (error: any) {
      console.error(`❌ Error marking page tutorial for ${page}:`, error.message);
    }
  }

  /**
   * Check if user should see page tutorials
   * Only new users (who have completed onboarding but not all page tutorials) see them
   */
  static async shouldShowPageTutorials(userId: string): Promise<boolean> {
    // Page tutorials are contextual, one-shot helpers: they must NOT depend on
    // the welcome tutorial or the onboarding questionnaire.
    //
    // They used to require `hasTutorialBeenShown`, which itself required the
    // questionnaire — so anyone who skipped either step never saw a single page
    // tutorial. Repeats are already prevented per page by
    // `hasPageTutorialBeenShown`, so showing them to every signed-in user is
    // both safe and what "an intro popup on each page" is supposed to mean.
    return Boolean(userId);
  }

  /**
   * Clear every page-tutorial flag so the popups play again from scratch.
   * Used by the "replay tutorials" action in Settings.
   *
   * Announces itself on success: `PageTourManager` stops querying progress once
   * every tour has been seen, and without a signal it would keep that opinion
   * for the rest of the session — the button would appear to do nothing until a
   * reload. Fired here rather than in the Settings component so any future
   * caller gets the same behaviour for free.
   */
  /**
   * Mark every tour as seen, in one go.
   *
   * The counterpart to `resetPageTutorials`. There was a button that switched
   * all the tours back on and nothing that switched them off: pressing Replay
   * out of curiosity armed seven cards across six pages, and the only way out
   * was to sit through every one. Someone who presses Skip is not asking to be
   * asked again on the next page.
   *
   * Writes are issued together and the result is reported, because a silent
   * failure here means the cards keep coming and the user has no idea why.
   */
  static async dismissAllTutorials(userId: string): Promise<{ ok: boolean }> {
    try {
      const results = await Promise.all(
        ALL_TOUR_PAGES.map((page) =>
          supabase.rpc('complete_tutorial_step', {
            p_user_id: userId,
            p_step_name: `page_tutorial_${page}`,
          })
        )
      );

      const failed = results.filter((r) => r.error);
      if (failed.length) {
        console.error('❌ dismissAllTutorials:', failed.map((r) => r.error?.message).join('; '));
        return { ok: false };
      }

      window.dispatchEvent(new Event(TUTORIALS_DISMISSED_EVENT));
      return { ok: true };
    } catch (error: any) {
      console.error('❌ dismissAllTutorials:', error?.message ?? error);
      return { ok: false };
    }
  }

  static async resetPageTutorials(userId: string): Promise<{ ok: boolean; cleared: number }> {
    // Prefer the SECURITY DEFINER RPC (migration 061): it reports how many rows
    // it cleared, so a silent no-op is visible instead of looking successful.
    try {
      const { data, error } = await supabase.rpc('reset_page_tutorials');
      if (!error && data?.ok) {
        window.dispatchEvent(new Event(TUTORIALS_RESET_EVENT));
        return { ok: true, cleared: Number(data.cleared ?? 0) };
      }
      if (error) console.warn('reset_page_tutorials RPC unavailable, falling back:', error.message);
    } catch {
      /* fall through to the direct delete */
    }

    // Fallback for databases where migration 061 has not been applied yet.
    // NOTE: without the DELETE policy from 061 this affects 0 rows and RLS
    // returns no error — which is exactly the bug 061 fixes.
    try {
      // 'first_run_tour' included so this matches what the RPC does — it
      // deletes on `page_tutorial_%`, which covers the first-run tour too.
      const steps = ALL_TOUR_PAGES.map((page) => `page_tutorial_${page}`);

      const { data, error } = await supabase
        .from('user_tutorial_progress')
        .delete()
        .eq('user_id', userId)
        .in('step_name', steps)
        .select('id');

      if (error) throw error;
      window.dispatchEvent(new Event(TUTORIALS_RESET_EVENT));
      return { ok: true, cleared: (data || []).length };
    } catch (error: any) {
      console.error('❌ Error resetting page tutorials:', error.message);
      return { ok: false, cleared: 0 };
    }
  }
}
